/**
 * Snap Dispatch — Plan 121 commit 3
 *
 * Given a click point (start or end of a route), determine how it should
 * connect into the graph. Three cases (see plan section "Plaza-aware snap
 * dispatch"):
 *
 *   A. Click outside any plaza, snap on a "line" edge.
 *      → Virtual node at snap point. Two virtual edges (virtual↔from,
 *        virtual↔to) with distances proportional to distAlong.
 *
 *   B. Click outside any plaza, snap on a "plaza-perimeter" edge.
 *      → Same edge-split as A, PLUS visibility-graph edges to every other
 *        candidate of the same plaza. Without this, the virtual perimeter
 *        node loses access to the interior diagonals that the perimeter
 *        vertex it would otherwise have been would have had — that's
 *        snap-position-dependent routing, exactly the class of bug we're
 *        eliminating.
 *
 *   C. Click inside any plaza polygon.
 *      → Virtual node at user's ACTUAL coordinate (not on any edge).
 *        Visibility-graph edges to all plaza candidates whose segment
 *        from the virtual doesn't cross any ring.
 *
 * Cross-snap cases handled after both snaps are determined:
 *   - Same edge: build a 3-segment chain (from ↔ low ↔ high ↔ to) with the
 *     middle segment being the direct virtual↔virtual edge. Don't add
 *     virtual↔far-endpoint shortcuts; they'd allow incorrect detour paths.
 *   - Same plaza interior: direct visibility edge between the two virtual
 *     interior nodes (in addition to each virtual's candidate edges from
 *     case C).
 *
 * Fast-path: if edge-snap lands within COORD_TOLERANCE of an existing node
 * (canonical equality via `coordToId`), use that node directly — no virtual
 * injection. Single source of truth for "are these the same point."
 */

import type { Feature, LineString } from "geojson";
import {
  booleanPointInPolygon,
  lineString as turfLineString,
  polygon as turfPolygon,
} from "@turf/turf";
import {
  createEmptyOverlay,
  edgeId,
  findNearestEdgePoint,
  isRouteDebugEnabled,
  segmentCrossesAnyRing,
  type EdgeSnapResult,
  type PathNetwork,
  type PlazaInfo,
  type RouteOverlay,
} from "./pathNetwork";
import { calcDistance, coordToId } from "./spatial";

export const VIRTUAL_START_ID = "wm-virtual-start";
export const VIRTUAL_END_ID = "wm-virtual-end";

/** Internal snap classification — drives overlay construction. */
type Snap =
  | { kind: "node"; nodeId: string; coord: [number, number] }
  | {
      kind: "edge";
      virtualId: string;
      coord: [number, number];
      edgeResult: EdgeSnapResult;
    }
  | {
      kind: "plaza-interior";
      virtualId: string;
      coord: [number, number];
      plaza: PlazaInfo;
    };

export interface SnapDispatchResult {
  overlay: RouteOverlay;
  /** Graph node ID where A* starts. May be a real or virtual node. */
  startNodeId: string;
  /** Graph node ID where A* terminates. */
  endNodeId: string;
}

/**
 * Build the route overlay for a (start, end) pair. Returns null only if the
 * network is empty enough that no snap is possible (no nodes, no edges, no
 * plazas).
 *
 * Non-mutating w.r.t. the input network — overlay is a fresh container.
 */
export function buildRouteOverlay(
  network: PathNetwork,
  start: [number, number],
  end: [number, number]
): SnapDispatchResult | null {
  const startSnap = determineSnap(network, start, VIRTUAL_START_ID);
  const endSnap = determineSnap(network, end, VIRTUAL_END_ID);
  if (!startSnap || !endSnap) return null;

  const overlay = createEmptyOverlay();

  // Cross-snap special cases. Detect BEFORE applying individual snaps so
  // same-edge gets the 3-segment chain instead of two independent splits.
  const sameEdge =
    startSnap.kind === "edge" &&
    endSnap.kind === "edge" &&
    startSnap.edgeResult.edgeId === endSnap.edgeResult.edgeId;

  const samePlazaInterior =
    startSnap.kind === "plaza-interior" &&
    endSnap.kind === "plaza-interior" &&
    startSnap.plaza.plazaId === endSnap.plaza.plazaId;

  if (sameEdge && startSnap.kind === "edge" && endSnap.kind === "edge") {
    applySameEdgeOverlay(network, overlay, startSnap, endSnap);
    // Plaza-perimeter same-edge keeps full Case B visibility for both ends.
    if (
      startSnap.edgeResult.edgeKind === "plaza-perimeter" &&
      startSnap.edgeResult.plazaId
    ) {
      const plaza = network.plazas.get(startSnap.edgeResult.plazaId);
      if (plaza) {
        addVisibilityConnections(overlay, startSnap.virtualId, startSnap.coord, plaza);
        addVisibilityConnections(overlay, endSnap.virtualId, endSnap.coord, plaza);
      }
    }
  } else {
    applyOneSnap(network, overlay, startSnap);
    applyOneSnap(network, overlay, endSnap);

    if (
      samePlazaInterior &&
      startSnap.kind === "plaza-interior" &&
      endSnap.kind === "plaza-interior"
    ) {
      addDirectInteriorVirtualEdge(overlay, startSnap, endSnap);
    }
  }

  return {
    overlay,
    startNodeId: snapNodeId(startSnap),
    endNodeId: snapNodeId(endSnap),
  };
}

// ---------------------------------------------------------------------------
// Snap determination
// ---------------------------------------------------------------------------

function determineSnap(
  network: PathNetwork,
  click: [number, number],
  virtualId: string
): Snap | null {
  // 1. Inside-plaza check. `ignoreBoundary: true` is load-bearing — a click
  // exactly on the perimeter (e.g., on a vertex shared by a plaza and a line
  // endpoint) should NOT be treated as a plaza-interior snap; it should fall
  // through to edge-snap and pick up the fast-path to the existing perimeter
  // node. Otherwise we'd add a 0-distance virtual edge from "wm-virtual-
  // start" to the very node the click landed on, polluting the path.
  // Document non-overlapping invariant; warn if multiple match when
  // wmRouteDebug is on (don't crash, pick first).
  const containing: PlazaInfo[] = [];
  for (const plaza of network.plazas.values()) {
    const polygon = turfPolygon(plaza.rings);
    if (booleanPointInPolygon(click, polygon, { ignoreBoundary: true })) {
      containing.push(plaza);
    }
  }
  if (containing.length > 1 && isRouteDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.warn("[pathNetwork] overlapping plazas at snap point — non-overlapping invariant violated", {
      plazaIds: containing.map((p) => p.plazaId),
      click,
    });
  }
  if (containing.length > 0) {
    return { kind: "plaza-interior", virtualId, coord: click, plaza: containing[0] };
  }

  // 2. Edge-snap.
  const snap = findNearestEdgePoint(network, click);
  if (!snap) {
    // No edges at all — but maybe there's still a node we should fall back to?
    // Realistic networks always have edges if they have nodes, so null here is
    // a degenerate input. Caller decides what to do.
    return null;
  }

  // 3. Fast-path: snap result is at an existing node by canonical equality.
  // This is the SINGLE-SOURCE-OF-TRUTH gate per Plan 121 — don't add a
  // parallel distance-then-rounded check, it'll disagree at the boundary.
  const existing = network.nodes.get(coordToId(snap.snapCoord));
  if (existing) {
    return { kind: "node", nodeId: existing.id, coord: existing.coords };
  }

  // 4. Edge-split snap (line or plaza-perimeter).
  return { kind: "edge", virtualId, coord: snap.snapCoord, edgeResult: snap };
}

function snapNodeId(snap: Snap): string {
  return snap.kind === "node" ? snap.nodeId : snap.virtualId;
}

// ---------------------------------------------------------------------------
// Overlay application — independent snap cases
// ---------------------------------------------------------------------------

function applyOneSnap(network: PathNetwork, overlay: RouteOverlay, snap: Snap): void {
  if (snap.kind === "node") return; // No virtual needed; the real node IS the endpoint.

  if (snap.kind === "edge") {
    injectEdgeSplit(network, overlay, snap.virtualId, snap.coord, snap.edgeResult);

    // Case B: plaza-perimeter gets full visibility to other plaza candidates.
    if (snap.edgeResult.edgeKind === "plaza-perimeter" && snap.edgeResult.plazaId) {
      const plaza = network.plazas.get(snap.edgeResult.plazaId);
      if (plaza) {
        addVisibilityConnections(overlay, snap.virtualId, snap.coord, plaza);
      }
    }
    return;
  }

  // Case C: plaza-interior.
  overlay.virtualNodes.set(snap.virtualId, {
    id: snap.virtualId,
    coords: snap.coord,
    connections: [],
  });
  addVisibilityConnections(overlay, snap.virtualId, snap.coord, snap.plaza);
}

/**
 * Edge-split injection (Case A and start of Case B).
 *
 * Adds: virtual node with [fromId, toId] as neighbors; two virtual edges
 * (virtual↔fromId, virtual↔toId) with distances proportional to distAlong;
 * extraConnections entries so fromId/toId discover the virtual as a neighbor
 * during A* traversal.
 *
 * Inherits the underlying edge's metadata so accessibility constraints
 * (hasStairs, steepGrade) propagate to the virtual edges. Without this, a
 * snap onto a stairs edge would become magically walkable in accessible mode.
 */
function injectEdgeSplit(
  network: PathNetwork,
  overlay: RouteOverlay,
  virtualId: string,
  coord: [number, number],
  snap: EdgeSnapResult
): void {
  const sourceEdge = network.edges.get(snap.edgeId);
  const inheritedMetadata = sourceEdge?.metadata;

  const distToFrom = snap.distAlong;
  const distToTo = Math.max(0, snap.edgeDist - snap.distAlong);

  overlay.virtualNodes.set(virtualId, {
    id: virtualId,
    coords: coord,
    connections: [snap.fromId, snap.toId],
  });

  overlay.virtualEdges.set(edgeId(virtualId, snap.fromId), {
    from: virtualId,
    to: snap.fromId,
    distance: distToFrom,
    kind: snap.edgeKind,
    plazaId: snap.plazaId,
    metadata: inheritedMetadata,
  });
  overlay.virtualEdges.set(edgeId(virtualId, snap.toId), {
    from: virtualId,
    to: snap.toId,
    distance: distToTo,
    kind: snap.edgeKind,
    plazaId: snap.plazaId,
    metadata: inheritedMetadata,
  });

  appendExtra(overlay, snap.fromId, virtualId);
  appendExtra(overlay, snap.toId, virtualId);
}

/**
 * Same-edge cross-snap (Case 5 in plan test matrix).
 *
 * Both start and end snap to the same edge. Order virtuals by distAlong
 * along the edge (low → high), then build a 3-segment chain:
 *   fromId ↔ virtualLow ↔ virtualHigh ↔ toId
 *
 * No virtual↔far-endpoint shortcuts. The original from↔to network edge is
 * left in place — A* may discover it but will never prefer it (it's always
 * longer than the chain through the virtuals).
 */
function applySameEdgeOverlay(
  network: PathNetwork,
  overlay: RouteOverlay,
  startSnap: Extract<Snap, { kind: "edge" }>,
  endSnap: Extract<Snap, { kind: "edge" }>
): void {
  const sA = startSnap.edgeResult;
  const sB = endSnap.edgeResult;

  let lowId: string;
  let highId: string;
  let lowCoord: [number, number];
  let highCoord: [number, number];
  let lowDist: number;
  let highDist: number;

  if (sA.distAlong <= sB.distAlong) {
    lowId = startSnap.virtualId;
    lowCoord = startSnap.coord;
    lowDist = sA.distAlong;
    highId = endSnap.virtualId;
    highCoord = endSnap.coord;
    highDist = sB.distAlong;
  } else {
    lowId = endSnap.virtualId;
    lowCoord = endSnap.coord;
    lowDist = sB.distAlong;
    highId = startSnap.virtualId;
    highCoord = startSnap.coord;
    highDist = sA.distAlong;
  }

  const { fromId, toId, edgeDist, edgeKind, plazaId } = sA;
  const sourceEdge = network.edges.get(sA.edgeId);
  const inheritedMetadata = sourceEdge?.metadata;

  overlay.virtualNodes.set(lowId, {
    id: lowId,
    coords: lowCoord,
    connections: [fromId, highId],
  });
  overlay.virtualNodes.set(highId, {
    id: highId,
    coords: highCoord,
    connections: [lowId, toId],
  });

  overlay.virtualEdges.set(edgeId(fromId, lowId), {
    from: fromId,
    to: lowId,
    distance: lowDist,
    kind: edgeKind,
    plazaId,
    metadata: inheritedMetadata,
  });
  overlay.virtualEdges.set(edgeId(lowId, highId), {
    from: lowId,
    to: highId,
    distance: Math.max(0, highDist - lowDist),
    kind: edgeKind,
    plazaId,
    metadata: inheritedMetadata,
  });
  overlay.virtualEdges.set(edgeId(highId, toId), {
    from: highId,
    to: toId,
    distance: Math.max(0, edgeDist - highDist),
    kind: edgeKind,
    plazaId,
    metadata: inheritedMetadata,
  });

  appendExtra(overlay, fromId, lowId);
  appendExtra(overlay, toId, highId);
}

/**
 * Add visibility-graph edges from a virtual node to every plaza candidate
 * whose connecting segment doesn't cross any ring. Used by Case B
 * (perimeter snap) and Case C (interior).
 *
 * Reuses `segmentCrossesAnyRing` so the visibility predicate matches what
 * `synthesizePolygonInteriorEdges` used at graph-construction time —
 * single-source-of-truth for "what can I see across this plaza."
 */
function addVisibilityConnections(
  overlay: RouteOverlay,
  virtualId: string,
  virtualCoord: [number, number],
  plaza: PlazaInfo
): void {
  const vnode = overlay.virtualNodes.get(virtualId);
  if (!vnode) return;

  for (const candidate of plaza.candidates) {
    const candidateLngLat: [number, number] = [candidate[0], candidate[1]];
    const candidateId = coordToId(candidateLngLat);
    if (candidateId === virtualId) continue;
    if (vnode.connections.includes(candidateId)) continue; // already wired (e.g. edge-split endpoint)

    const segment: Feature<LineString> = turfLineString([virtualCoord, candidateLngLat]);
    if (segmentCrossesAnyRing(segment, virtualCoord, candidateLngLat, plaza.ringLineStrings)) {
      continue;
    }

    const dist = calcDistance(virtualCoord, candidateLngLat);
    vnode.connections.push(candidateId);
    appendExtra(overlay, candidateId, virtualId);
    overlay.virtualEdges.set(edgeId(virtualId, candidateId), {
      from: virtualId,
      to: candidateId,
      distance: dist,
      kind: "plaza-interior",
      plazaId: plaza.plazaId,
    });
  }
}

/**
 * Same-plaza-interior cross-snap (Case 7 in plan test matrix).
 *
 * Two virtual interior nodes already exist (each connected to their own
 * candidates via Case C). If the segment between them doesn't cross a ring,
 * add a direct virtual-to-virtual edge so A* can take the straight-line
 * route across the plaza interior.
 */
function addDirectInteriorVirtualEdge(
  overlay: RouteOverlay,
  startSnap: Extract<Snap, { kind: "plaza-interior" }>,
  endSnap: Extract<Snap, { kind: "plaza-interior" }>
): void {
  const segment: Feature<LineString> = turfLineString([startSnap.coord, endSnap.coord]);
  if (segmentCrossesAnyRing(segment, startSnap.coord, endSnap.coord, startSnap.plaza.ringLineStrings)) {
    return;
  }

  const dist = calcDistance(startSnap.coord, endSnap.coord);
  overlay.virtualEdges.set(edgeId(startSnap.virtualId, endSnap.virtualId), {
    from: startSnap.virtualId,
    to: endSnap.virtualId,
    distance: dist,
    kind: "plaza-interior",
    plazaId: startSnap.plaza.plazaId,
  });

  const sNode = overlay.virtualNodes.get(startSnap.virtualId);
  const eNode = overlay.virtualNodes.get(endSnap.virtualId);
  if (sNode && !sNode.connections.includes(endSnap.virtualId)) {
    sNode.connections.push(endSnap.virtualId);
  }
  if (eNode && !eNode.connections.includes(startSnap.virtualId)) {
    eNode.connections.push(startSnap.virtualId);
  }
}

function appendExtra(overlay: RouteOverlay, nodeId: string, virtualId: string): void {
  const list = overlay.extraConnections.get(nodeId) ?? [];
  if (!list.includes(virtualId)) {
    list.push(virtualId);
  }
  overlay.extraConnections.set(nodeId, list);
}

